import {
  CustomMouseEvent,
  defaultStrokeColor,
  mapActive,
  mapGradient,
  mapGradientStep,
  notCodedColor,
} from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { RootStore } from "../../shared/stores/RootStore";
import { MapMode } from "../../survey/stores/MapStore";
import { HlaLane } from "../../hla/models/HlaLane";
import { HlaWay } from "../../hla/models/HlaWay";
import {
  coloringSteps,
  getHlaAttributeValueForColoring,
} from "../models/HlaColoring";
import { HlaAttributeName, hlaAttributeOptions } from "../models/HlaAttribute";
import { MapColoringType } from "../models/MapColoring";

export class HlaMapSource {
  map: mapboxgl.Map;
  active = false;
  rootStore: RootStore;
  lineClickListenerRemovers: (() => void)[] = [];

  strokeColor = defaultStrokeColor;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(strokeLayerConfig);
      this.map.addLayer(lineMultiSelectedLayerConfig);
      this.map.addLayer(rightLineLayerConfig);
      this.map.addLayer(leftLineLayerConfig);
      this.map.addLayer(directionLayerConfig);
    }
  }

  onModeChange(mode: MapMode) {
    if (mode === MapMode.HomePage) {
      this.setMapData([]);
      this.active = false;
    } else {
      this.updateMapData();
      this.active = true;
    }

    this.disableLineClick();
    this.disableMapClick();
    if (mode === MapMode.SurveyPage || mode === MapMode.HlaCoding) {
      this.enableLineClick();
    }
    if (mode === MapMode.HlaCoding) {
      this.enableMapClick();
    }
  }

  onStoreUpdate() {
    if (!this.active) return;

    this.updateMapData();
  }

  clear() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];

    if (mapActive(this.map)) {
      this.map.removeLayer(strokeLayerConfig.id);
      this.map.removeLayer(leftLineLayerConfig.id);
      this.map.removeLayer(rightLineLayerConfig.id);
      this.map.removeLayer(directionLayerConfig.id);
      this.map.removeLayer(lineMultiSelectedLayerConfig.id);
      this.map.removeSource(sourceName);
    }
  }

  private updateMapData() {
    const { hlaStore, filterStore, surveyStore } = this.rootStore;
    const rightHandTraffic = surveyStore.rightHandTraffic;

    const selectedWay = hlaStore.getSelected();
    this.setMapData(
      filterStore.showHla
        ? hlaStore.getWays().map((way) => {
            const showWay = filterStore.filterHlaWay(way);

            const frontFacingColor = showWay
              ? this.getLineColor(way, way.frontFacingLane)
              : "transparent";

            const backFacingColor = showWay
              ? this.getLineColor(way, way.backFacingLane)
              : "transparent";

            var strokeWidth = 10;
            if (selectedWay?.id === way.id) {
              strokeWidth = 15;
            }

            const leftColor = rightHandTraffic
              ? backFacingColor
              : frontFacingColor;
            const rightColor = rightHandTraffic
              ? frontFacingColor
              : backFacingColor;

            const properties: HlaLineProps = {
              leftColor,
              hideLeftLane: leftColor === "transparent",
              rightColor,
              hideRightLane: rightColor === "transparent",
              strokeColor: defaultStrokeColor,
              strokeWidth: strokeWidth,
              hlaWayId: way.id,
              isMultiSelected: hlaStore.multiSelectedIds.has(way.id),
            };
            return {
              type: "Feature",
              properties,
              geometry: {
                type: "LineString",
                coordinates: way.coordinates,
              },
            };
          })
        : [],
    );
  }

  private setMapData(features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  getLineColor(way: HlaWay, lane: HlaLane | null): string {
    const { filterStore, mapStore } = this.rootStore;

    if (!lane || !filterStore.filterHlaLane(lane)) return "transparent";

    const coloring = mapStore.coloring;

    if (coloring.type === MapColoringType.Score) {
      if (!lane.score) return notCodedColor;

      const scoreEntry = lane.score[coloring.scoreKey];
      return scoreEntry.color;
    }

    if (coloring.type === MapColoringType.HlaAttribute) {
      const value = getHlaAttributeValueForColoring(
        coloring.attributeName,
        way,
        lane,
      );

      if (
        coloring.attributeName === HlaAttributeName.AverageDailyTraffic ||
        coloring.attributeName === HlaAttributeName.AverageRoadSpeed
      ) {
        if (value == null) return notCodedColor;
        const steps = coloringSteps[coloring.attributeName]!;

        const step = steps.find((_, index) => {
          const nextStep = steps[index + 1];
          if (nextStep === undefined) return true;

          if (value < nextStep) return true;

          return false;
        })!;

        return `#${mapGradient.hsvAt(step / steps[steps.length - 1]).toHex()}`;
      } else {
        if (value == null) return notCodedColor;

        const options = hlaAttributeOptions[coloring.attributeName]!;
        const length = options.length;
        const optionIndex = options.findIndex(
          (option) => option.value === value,
        );
        return mapGradientStep(length, optionIndex);
      }
    }

    return notCodedColor;
  }

  enableLineClick() {
    const { hlaStore, streetViewStore } = this.rootStore;
    const canvas = this.map.getCanvasContainer();

    const onMouseDown = (e: CustomMouseEvent) => {
      e.preventDefault();
      canvas.style.cursor = "grab";
      const feature = e.features?.[0]!;
      const props = feature.properties as HlaLineProps;

      this.map.once("mouseup", () => {
        canvas.style.cursor = "";
        hlaStore.select(props.hlaWayId);
        const hlaWay = hlaStore.get(props.hlaWayId);
        hlaWay && streetViewStore.goToHla(hlaWay);
      });
    };

    const cursorPointer = () => (canvas.style.cursor = "pointer");
    const cursorDefault = () => (canvas.style.cursor = "");

    this.map.on("mouseenter", strokeLayerConfig.id, cursorPointer);
    this.map.on("mouseleave", strokeLayerConfig.id, cursorDefault);
    this.map.on("mousedown", strokeLayerConfig.id, onMouseDown);

    this.lineClickListenerRemovers.push(() => {
      this.map.off("mouseenter", strokeLayerConfig.id, cursorPointer);
      this.map.off("mouseleave", strokeLayerConfig.id, cursorDefault);
      this.map.off("mousedown", strokeLayerConfig.id, onMouseDown);
    });
  }

  disableLineClick() {
    this.lineClickListenerRemovers.map((fn) => fn());
    this.lineClickListenerRemovers = [];
  }

  enableMapClick() {
    const { streetViewStore } = this.rootStore;
    this.map.addInteraction("map-click", {
      type: "click",
      handler: (e) => {
        console.log(`Moving StreetView to: ${e.lngLat.lng}, ${e.lngLat.lat}`);
        streetViewStore.client?.setPosition(e.lngLat);
      },
    });
  }

  disableMapClick() {
    this.map.removeInteraction("map-click");
  }

  extendBounds(bounds: mapboxgl.LngLatBounds) {
    const { craNodeStore, craWayStore, hlaStore, filterStore } = this.rootStore;

    const selectedCraNode = craNodeStore.getSelected();
    const selectedCraWay = craWayStore.getSelected();
    const selectedHlaWay = hlaStore.getSelected();
    const hlaWays = filterStore.filteredHlaWays;

    if (selectedCraNode || selectedCraWay) return;
    hlaWays.forEach((hlaWay) => {
      if (selectedHlaWay && selectedHlaWay.id !== hlaWay.id) return;

      hlaWay.coordinates.forEach((coords) => {
        bounds.extend(coords as [number, number]);
      });
    });
  }
}

const isLineString = ["==", "$type", "LineString"];
const showRightLane = ["!=", "hideRightLane", "false"];
const showLeftLane = ["!=", "hideLeftLane", "false"];
const isMultiNodeSelected = ["boolean", ["get", "isMultiSelected"], false];

const sourceName = CustomSource.Hla;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  promoteId: "id",
  data: {
    type: "FeatureCollection",
    features: [],
  },
};

const rightLineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-right-line`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "rightColor"],
    "line-width": 4,
    "line-offset": 2,
  },
  filter: ["all", isLineString, showRightLane],
};

const leftLineLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-left-line`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "leftColor"],
    "line-width": 4,
    "line-offset": -2,
  },
  filter: ["all", isLineString, showLeftLane],
};

const strokeLayerConfig: mapboxgl.LineLayerSpecification = {
  id: `${sourceName}-stroke`,
  source: sourceName,
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  paint: {
    "line-color": ["get", "strokeColor"],
    "line-width": ["get", "strokeWidth"],
  },
  filter: isLineString,
};

// Adds arrows to the way lines so that you know the direction
const directionLayerName = `${sourceName}-direction`;
const directionLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: directionLayerName,
  source: sourceName,
  type: "symbol",
  layout: {
    "symbol-placement": "line",
    "symbol-spacing": 50,
    "icon-image": "chevron-icon",
    "icon-rotation-alignment": "map",
    "icon-size": 0.5,
  },
  paint: {
    "icon-color": defaultStrokeColor,
  },
};

// The blue line that appears when you select multiple ways
const lineMultiSelectedLayerConfig: mapboxgl.LineLayerSpecification = {
  type: "line",
  layout: {
    "line-join": "round",
    "line-cap": "round",
  },
  id: `${sourceName}-multi-selected-line`,
  source: sourceName,
  paint: {
    "line-width": ["case", isMultiNodeSelected, ["get", "strokeWidth"], 0],
    "line-color": "#00b7db",
  },
  filter: isLineString,
};

export interface HlaLineProps {
  hlaWayId: string;
  hideLeftLane: boolean;
  leftColor: string;
  rightColor: string;
  hideRightLane: boolean;
  strokeColor: string;
  strokeWidth: number;
  isMultiSelected: boolean;
  "score.global"?: string;
  "score.bicycleToBicycle"?: string;
  "score.bicycleToPedestrian"?: string;
  "score.singleBicycle"?: string;
  "score.vehicleToBicycle"?: string;
}

export const hlaWayShiftDragLayerNames = [strokeLayerConfig.id];
export const hlaWayLineLayerName = strokeLayerConfig.id;
